import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.js';
import {
  blockedDomains,
  UKRAINE_CODE,
  SPECIAL_CITIES_WITHOUT_DISTRICTS,
} from '~/constants/common.js';
import { parseDate } from '~/helpers/utils';
import {
  EXPIRY_DOCUMENT_TYPES,
  IDENTITY_DOCS,
  IdentityDocumentType,
  ISSUE_DATE_REQUIRED_DOC_TYPES,
} from '~/constants/docs.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { checkTableField } from './utils';
import { latinAndUkrCharactersOnly } from '~/helpers/validation';
import { TFormAddress } from './types';

export const requiredOptionSchema = Yup.object()
  .shape({
    value: Yup.string().nullable(),
    label: Yup.string().required(errorMessages.FIELD_REQUIRED),
  })
  .nullable()
  .required(errorMessages.FIELD_REQUIRED);

export const notRequiredOptionSchema = Yup.object()
  .shape({
    value: Yup.string().nullable(),
    label: Yup.string().nullable(),
  })
  .nullable()
  .notRequired();

export const addressFieldSchema = Yup.string()
  .test('address-required', errorMessages.ADDRESS_FIELDS, function () {
    const {
      country,
      region,
      city,
      street,
      postalCode,
      houseNumber,
      building,
      apartment,
      district,
    }: TFormAddress = this.parent;

    const validatePostalCode =
      country?.value === UKRAINE_CODE
        ? postalCode && regularExpressions.POSTAL_CODE_DIGITS.test(postalCode)
        : true;
    const validateRegion = country?.value === UKRAINE_CODE ? region?.label : true;
    const isCity = SPECIAL_CITIES_WITHOUT_DISTRICTS.includes(region?.value || '');

    const validateDistrict = country?.value === UKRAINE_CODE && !isCity ? district?.label : true;
    const validateHouseNumber = houseNumber && houseNumber.length <= 10;
    const validateBuilding = building ? building.length <= 10 : true;
    const validateApartment = apartment ? apartment.length <= 10 : true;

    const isValid =
      country?.label &&
      validateRegion &&
      city?.label &&
      street?.label &&
      validatePostalCode &&
      validateHouseNumber &&
      validateBuilding &&
      validateDistrict &&
      validateApartment;
    if (!isValid) {
      return this.createError({
        path: this.path,
        message: errorMessages.ADDRESS_FIELDS,
      });
    }
    return true;
  })
  .nullable();

export const regionFieldSchema = Yup.object().when(['country'], ([country]) => {
  if (country?.value === UKRAINE_CODE) {
    return requiredOptionSchema;
  } else {
    return notRequiredOptionSchema;
  }
});

export const postalCodeFieldSchema = Yup.string().when(['country'], ([country], schema) => {
  if (country?.value === UKRAINE_CODE) {
    return schema
      .required(errorMessages.FIELD_REQUIRED)
      .matches(regularExpressions.POSTAL_CODE_DIGITS, errorMessages.POSTAL_CODE_ONLY_NUMBERS);
  } else return schema.nullable();
});

export const districtFieldSchema = Yup.object().when(['region', 'country'], ([region, country]) => {
  const isCity = SPECIAL_CITIES_WITHOUT_DISTRICTS.includes(region?.value);

  if (country?.value === UKRAINE_CODE && !isCity) {
    return requiredOptionSchema;
  } else {
    return notRequiredOptionSchema;
  }
});

export const addressSchema = Yup.object().shape({
  address: addressFieldSchema,
  country: Yup.object()
    .shape({
      value: Yup.string().nullable(),
      label: Yup.string().required(errorMessages.OPTION_REQUIRED),
    })
    .required(errorMessages.OPTION_REQUIRED),
  postalCode: postalCodeFieldSchema,
  region: regionFieldSchema,
  district: districtFieldSchema,
  city: requiredOptionSchema,
  street: requiredOptionSchema,
  houseNumber: Yup.string()
    .max(10, errorMessages.MAX_LENGTH(10))
    .required(errorMessages.FIELD_REQUIRED),
  building: Yup.string().max(10, errorMessages.MAX_LENGTH(10)).nullable(),
  apartment: Yup.string().max(10, errorMessages.MAX_LENGTH(10)).nullable(),
  codeAddress: Yup.string().nullable(),
});

export const addressNotRequiredSchema = Yup.object().shape({
  address: Yup.string().notRequired().nullable(),
  country: notRequiredOptionSchema,
  postalCode: Yup.string().nullable().notRequired(),
  region: notRequiredOptionSchema,
  district: notRequiredOptionSchema,
  city: notRequiredOptionSchema,
  street: notRequiredOptionSchema,
  houseNumber: Yup.string().nullable().notRequired(),
  building: Yup.string().nullable().notRequired(),
  apartment: Yup.string().nullable().notRequired(),
  codeAddress: Yup.string().notRequired(),
});

export const licensesSchema = (dependencyField: string) =>
  Yup.array().of(
    Yup.object()
      .shape({
        licenseName: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, dependencyField, value);
          }),
        licenseDate: Yup.string()
          .nullable()
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, dependencyField, value);
          }),
        licenseNumber: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, dependencyField, value);
          }),
        licenseOrganisationCreate: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, dependencyField, value);
          }),
        licenseExpiryTerm: Yup.string().nullable(),
      })
      .test('date-check', function (value) {
        if (value?.licenseDate && value?.licenseExpiryTerm) {
          const licenseDate = parseDate(value.licenseDate) as Date;
          const licenseExpiryTerm = parseDate(value?.licenseExpiryTerm) as Date;

          if (licenseDate > licenseExpiryTerm) {
            return this.createError({
              path: `${this.path}.licenseExpiryTerm`,
              message: errorMessages.INVALID_DATE,
            });
          }
        }
        return true;
      })
  );

export const documentNumberSchema = Yup.string()
  .nullable()
  .when(['documentType'], ([docType], schema) => {
    if (
      [
        IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassport],
        IDENTITY_DOCS[IdentityDocumentType.ukrainianForeignPassport],
        IDENTITY_DOCS[IdentityDocumentType.permanentResidencePermit],
        IDENTITY_DOCS[IdentityDocumentType.temporaryResidencePermit],
      ].includes(+docType?.value)
    ) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(regularExpressions.SIX_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(6))
        .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
          if (!value) return true;
          return !regularExpressions.DUPLICATION_NUMBER.test(value);
        });
    }
    if (
      [
        IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassportPlastic],
        IDENTITY_DOCS[IdentityDocumentType.permanentResidencePermitPlastic],
        IDENTITY_DOCS[IdentityDocumentType.temporaryResidencePermitPlastic],
      ].includes(+docType?.value)
    ) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(regularExpressions.NINE_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(9))
        .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
          if (!value) return true;
          return !regularExpressions.DUPLICATION_NUMBER.test(value);
        });
    }
    if (
      [
        IDENTITY_DOCS[IdentityDocumentType.nonResidentNationalPassport],
        IDENTITY_DOCS[IdentityDocumentType.foreignCitizenIdCard],
      ].includes(+docType?.value)
    ) {
      return schema
        .matches(regularExpressions.ALLOWED_CHARS, errorMessages.ALLOWED_CHARS_SYMBOLS_ONLY)
        .max(20, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(20))
        .notRequired();
    }
    return schema;
  });

export const documentSeriesSchema = Yup.string()
  .nullable()
  .when(['documentType'], ([docType], schema) => {
    if (
      [
        IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassport],
        IDENTITY_DOCS[IdentityDocumentType.permanentResidencePermit],
        IDENTITY_DOCS[IdentityDocumentType.temporaryResidencePermit],
      ].includes(+docType?.value)
    ) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(
          regularExpressions.UPPER_CASE_UKRAINIAN_LETTERS,
          errorMessages.TWO_UPPER_CASE_UKRAINIAN_LETTERS
        );
    } else if (IDENTITY_DOCS[IdentityDocumentType.ukrainianForeignPassport] === +docType?.value) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(regularExpressions.LATIN_TWO_LETTERS, errorMessages.TWO_LATIN_LETTERS);
    } else if (
      [
        IDENTITY_DOCS[IdentityDocumentType.nonResidentNationalPassport],
        IDENTITY_DOCS[IdentityDocumentType.foreignCitizenIdCard],
      ].includes(+docType?.value)
    ) {
      return schema
        .notRequired()
        .max(8, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(8))
        .test('only-one-alphabet', errorMessages.UPPER_CASE_LETTERS_ONLY_OR, (value) => {
          if (!value?.trim()) return true;

          return (
            regularExpressions.LATIN_UPPERCASE.test(value) ||
            regularExpressions.CYRILLIC_UPPERCASE.test(value)
          );
        });
    } else return schema.notRequired();
  });

export const issuingAuthoritySchema = Yup.string()
  .nullable()
  .when(['documentType'], ([docType], schema) => {
    if (+docType?.value === IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassport]) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .max(120, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(120))
        .matches(regularExpressions.AUTHORITY_LETTERS, errorMessages.AUTHORITY_ONLY_LETTERS);
    } else if (+docType?.value === IDENTITY_DOCS[IdentityDocumentType.ukrainianForeignPassport]) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .max(4, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(4))
        .matches(
          regularExpressions.UPPER_CASE_LATIN_AND_DIGITS,
          errorMessages.UPPER_CASE_LATIN_AND_DIGITS
        );
    } else if (EXPIRY_DOCUMENT_TYPES.includes(+docType?.value)) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(regularExpressions.AUTHORITY_FOUR_DIGITS, errorMessages.AUTHORITY_ONLY_NUMBERS);
    }
    return schema;
  });

export const documentExpiryDateSchema = Yup.string().when(['documentType'], ([docType], schema) => {
  if (EXPIRY_DOCUMENT_TYPES.includes(+docType?.value)) {
    return schema.nullable().required(errorMessages.FIELD_REQUIRED);
  } else {
    return schema.nullable().notRequired();
  }
});

export const documentIssueDateSchema = Yup.string().when(['documentType'], ([docType], schema) => {
  if (ISSUE_DATE_REQUIRED_DOC_TYPES.includes(+docType?.value)) {
    return schema.nullable().required(errorMessages.FIELD_REQUIRED);
  } else {
    return schema.nullable().notRequired();
  }
});

export const registryRecordSchema = Yup.string()
  .nullable()
  .when(['documentType'], ([docType], schema, ctx) => {
    if (EXPIRY_DOCUMENT_TYPES.includes(+docType?.value)) {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(regularExpressions.REGISTRY_RECORD, errorMessages.REGISTER_ENTRY_FORMAT);
    } else if (!ctx.value) {
      return schema.notRequired();
    } else {
      return schema
        .required(errorMessages.FIELD_REQUIRED)
        .matches(regularExpressions.REGISTRY_RECORD, errorMessages.REGISTER_ENTRY_FORMAT);
    }
  });

export const nameSchema = Yup.string()
  .nullable()
  .transform((_, orin) => (orin?.trim() === '' ? null : orin))
  .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT)
  .concat(latinAndUkrCharactersOnly);

export const documentSchema = Yup.object().shape({
  documentType: requiredOptionSchema,
  documentSeries: documentSeriesSchema,
  documentNumber: documentNumberSchema,
  issueDate: documentIssueDateSchema,
  issuingAuthority: issuingAuthoritySchema,
  documentExpiryDate: documentExpiryDateSchema,
  registryRecord: registryRecordSchema,
});

export const documentNotRequiredSchema = Yup.object().shape({
  documentType: notRequiredOptionSchema,
  documentSeries: documentSeriesSchema.notRequired(),
  documentNumber: documentNumberSchema.notRequired(),
  issueDate: Yup.string().notRequired(),
  issuingAuthority: issuingAuthoritySchema.notRequired(),
  documentExpiryDate: documentExpiryDateSchema.notRequired(),
  registryRecord: registryRecordSchema.notRequired(),
});

export const emailSchema = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('contains-at', function (value) {
    if (!value.includes('@')) {
      return this.createError({
        path: this.path,
        message: errorMessages.MISSING_SYMBOL_WITH_VALUE(value),
      });
    }
    return true;
  })
  .test('blocked-domain', errorMessages.BLOCKED_EMAIL_DOMAIN, function (value) {
    const domain = value.split('@')[1];
    const isBlocked = blockedDomains.some(
      (blocked) => domain === blocked || domain?.endsWith(`.${blocked}`)
    );

    if (isBlocked) {
      return this.createError({
        path: this.path,
        message: errorMessages.BLOCKED_EMAIL_DOMAIN,
      });
    }
    return true;
  })
  .email(errorMessages.INVALID_EMAIL_FORMAT);

export const phoneSchema = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .matches(/^\+?[\d\s\-()]+$/, errorMessages.FIELD_REQUIRED)
  .test('phone-validation', function (value) {
    if (!value) return true;
    const cleanPhone = value.replace(/[\s\-()]/g, '');

    if (cleanPhone.startsWith('+380')) {
      const numberPart = cleanPhone.substring(4);
      const digitsOnly = numberPart.replace(/\D/g, '');
      const isCorrect = digitsOnly.length >= 9;
      if (!isCorrect) {
        return this.createError({
          path: this.path,
          message: errorMessages.INVALID_INPUT_DATA,
        });
      }
    } else {
      const numberPart = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : cleanPhone;
      const digitsOnly = numberPart.replace(/\D/g, '');
      const isCorrect = digitsOnly.length >= 7;
      if (!isCorrect) {
        return this.createError({
          path: this.path,
          message: errorMessages.INVALID_INPUT_DATA,
        });
      }
    }
    return true;
  });

export const RNOKPPIdentNumberSchema = (dependentField?: string, isAbsent?: boolean) => {
  return identificationNumberSchema(dependentField, isAbsent).test(
    'is-length-10',
    function (value) {
      if (value && value.trim() !== '') {
        if (value.trim().length !== 10) {
          return this.createError({
            message: errorMessages.MAX_LENGTH(10),
            path: this.path,
          });
        }
      }
      return true;
    }
  );
};

export const identificationNumberSchema = (dependentField?: string, isAbsent?: boolean) => {
  return Yup.string()
    .nullable()
    .test('required-if-both-empty', function (value) {
      if (!dependentField || isAbsent) return true;
      const field = this.parent[dependentField];
      if ((!value || value.trim() === '') && (!field || field.trim() === '')) {
        return this.createError({
          message: errorMessages.FIELD_REQUIRED,
          path: this.path,
        });
      }
      return true;
    })
    .test('is-numeric', function (value) {
      if (value && value.trim() !== '') {
        if (!regularExpressions.IDENTIFICATION_NUMBER.test(value)) {
          return this.createError({
            message: errorMessages.MUST_BE_NUMBER,
            path: this.path,
          });
        }
      }
      return true;
    });
};

export const foreignTaxNumberSchema = (dependentField?: string, isAbsent?: boolean) => {
  return Yup.string()
    .nullable()
    .max(20, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(20))
    .matches(regularExpressions.LATIN_NUMBERS_SYMBOLS, errorMessages.LATIN_NUMBERS_SYMBOLS_ONLY)
    .test('required-if-both-empty', function (value) {
      if (!dependentField || isAbsent) return true;
      const field = this.parent[dependentField];
      if ((!value || value.trim() === '') && (!field || field.trim() === '')) {
        return this.createError({
          message: errorMessages.FIELD_REQUIRED,
          path: this.path,
        });
      }
      return true;
    });
};
